/**
 * useSpacesState — load and manage the Spaces envelope (multiple named boards).
 *
 * Load order (per the tech-lead handoff / spaces-migrate.loadSpaces contract):
 *   1. Parse SPACES_STORAGE_KEY → ladder → validate → 'spaces' source.
 *   2. Absent spaces key → parse BOARD_STORAGE_KEY → legacy lift → 'legacy' source.
 *   3. Both absent / invalid → seed a default "Home" space (buildDefaultBoard).
 *   4. future-version → surface to AppShell (friendly screen, no mangle).
 *
 * NEW-SPACE BOARD RULING (documented):
 *   A new space starts as an EMPTY board (tiles: [], layout: []).
 *   Rationale: BoardSchema allows 0 tiles (no minimum on the tiles array), so an
 *   empty board is schema-valid. An empty space presents the add-tile affordance,
 *   giving users "freedom" — they decide what goes there. This is the "blank canvas"
 *   model, not a forced default. Confirmed by checking BoardSchema: tiles field uses
 *   `.max(MAX_TILES_PER_BOARD)` with no `.min()`, so tiles:[] is valid.
 *
 * PERSIST: only to SPACES_STORAGE_KEY going forward. The legacy slot is never
 * written again (the board-key slot stays unchanged so a downgrade can still find it).
 *
 * Immutability: every mutation routes through the ops layer (spaces-ops.ts), which
 * returns a new Spaces envelope. setState always receives a fresh reference.
 *
 * CRITICAL-1: corrupt spaces slot + valid legacy board → recovery.
 *   When the spaces slot is non-null but invalid, we attempt the legacy path.
 *   If legacy succeeds → mode: 'recovered-legacy' (overwrite the corrupt slot).
 *   If no recovery is possible → mode: 'error-preserve' (DO NOT seed fresh).
 *
 * CRITICAL-2: persistSpaces quota failure.
 *   persistSpaces returns boolean. On failure, persistFailed is set so the App
 *   can show a calm banner. In-memory state still advances so the user's edits
 *   are not lost. A successful subsequent commit clears the flag.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  loadSpaces,
  createSpace,
  renameSpace,
  deleteSpace,
  duplicateSpace,
  reorderSpaces,
  setActiveSpace,
  updateSpaceBoard,
  SpacesSchema,
  SPACES_STORAGE_KEY,
  BOARD_STORAGE_KEY,
  BOARD_SCHEMA_VERSION,
  SPACES_SCHEMA_VERSION,
  type Spaces,
  type Space,
  type Board,
} from 'briefpane/schema';
import type { AnalyticsEmitter } from 'briefpane/contracts';
import { buildEnvelope } from '../analytics/identity.js';
import { buildDefaultBoard } from './default-board.js';
import { generateSpaceId, generateBoardId } from './spaces-id.js';
import {
  spaceCreatedEvent,
  spaceSwitchedEvent,
  spaceRenamedEvent,
  spaceDeletedEvent,
  spaceDuplicatedEvent,
  spaceReorderedEvent,
  withEnvelope,
} from './spaces-analytics-events.js';

/** Safely emit one analytics event; never throws. */
function safeEmit(emitter: AnalyticsEmitter, ev: Parameters<AnalyticsEmitter['emit']>[0]): void {
  if (!emitter.enabled) return;
  try { emitter.emit(ev); } catch { /* analytics never degrades the product */ }
}

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

export type SpacesStatus =
  | 'loading'
  | 'ready'
  | 'future-version'
  | 'error';

/** Typed surface for inline UI messages (name validation, limit notes). */
export type SpacesActionError =
  | 'duplicate-name'
  | 'max-spaces'
  | 'last-space'
  | 'unknown-space'
  | 'id-collision'
  | 'invalid'
  | null;

export interface SpacesActions {
  /**
   * Switch to a space by id. No-op if id is unknown OR if the space is already
   * active (prevents firing space_switched when clicking the active tab).
   * `method` is REQUIRED from the call-site so the analytics event accurately
   * records which navigation surface the user used. Pass null to suppress the
   * space_switched event (used for programmatic/TV URL resolution — not user-initiated).
   */
  readonly switchSpace: (spaceId: string, method: 'tab' | 'pill' | 'shortcut' | null) => void;
  /**
   * Create a new space with the given name (must be pre-trimmed by caller).
   * The new space starts with an EMPTY board (ruling above).
   * Returns null on success, or a typed error for inline UI message.
   */
  readonly createSpace: (name: string) => SpacesActionError;
  /**
   * Rename a space. Name must be pre-trimmed by caller.
   * `method` is REQUIRED from the call-site: 'inline' for double-click rename,
   * 'popover' for the popover dialog rename.
   * Returns null on success, or a typed error.
   */
  readonly renameSpace: (spaceId: string, newName: string, method: 'inline' | 'popover') => SpacesActionError;
  /**
   * Delete a space. Rejects if it is the last space.
   * Returns null on success, or a typed error.
   */
  readonly deleteSpace: (spaceId: string) => SpacesActionError;
  /**
   * Duplicate a space. The copy gets a new space id, a new board id, but keeps
   * source tile ids (per-board id namespace — see spaces-ops.ts).
   * Returns null on success, or a typed error.
   */
  readonly duplicateSpace: (sourceSpaceId: string) => SpacesActionError;
  /**
   * THE BRIDGE: fold a new (already board-op-validated) Board into the active
   * space. Called by useEditableBoard and OnboardingBar after every commit.
   * Returns true if the spaces envelope was successfully updated; false if the
   * active space was not found (race: user switched away before commit landed).
   */
  readonly updateActiveBoard: (newBoard: Board) => boolean;
  /**
   * Reorder spaces to match orderedIds (exact permutation of current ids).
   * `fromIndex` and `toIndex` are the zero-based positions before and after
   * reorder — passed from the drag-commit site for analytics.
   * Returns null on success, or a typed error.
   */
  readonly reorderSpaces: (
    orderedIds: readonly string[],
    fromIndex?: number,
    toIndex?: number,
  ) => SpacesActionError;
}

export interface SpacesState {
  readonly status: SpacesStatus;
  readonly spaces: readonly Space[];
  readonly activeSpace: Space | null;
  readonly actions: SpacesActions;
  /** Non-null on error status — passed to AppShell for display. */
  readonly errorDetail: string | null;
  /**
   * True when the last persistSpaces call failed (quota exceeded / storage
   * unavailable). App shows a calm dismissible banner. Clears on next success.
   */
  readonly persistFailed: boolean;
}

/* -------------------------------------------------------------------------- */
/* Boot decision types (CRITICAL-1)                                            */
/* -------------------------------------------------------------------------- */

export type BootDecision =
  | { mode: 'ok'; spaces: Spaces; source: 'spaces' | 'legacy' }
  | { mode: 'recovered-legacy'; spaces: Spaces }
  | { mode: 'seed-fresh' }
  | { mode: 'error-preserve'; reason: 'future-version' | 'invalid'; detail?: string };

/**
 * Pure function: given the two raw storage blobs, decide how to boot.
 *
 * Decision table:
 *   - Both null                           → seed-fresh
 *   - rawSpaces null, rawLegacy valid     → ok/legacy  (normal first-run migration)
 *   - rawSpaces null, rawLegacy invalid   → seed-fresh (designed failure — both absent effectively)
 *   - rawSpaces null, rawLegacy future    → error-preserve/future-version
 *   - rawSpaces valid                     → ok/spaces
 *   - rawSpaces future-version            → error-preserve/future-version
 *   - rawSpaces invalid, rawLegacy valid  → recovered-legacy  (CRITICAL-1 fix)
 *   - rawSpaces invalid, rawLegacy null   → error-preserve/invalid  (DO NOT seed-fresh)
 *   - rawSpaces invalid, rawLegacy invalid → error-preserve/invalid
 *   - rawSpaces invalid, rawLegacy future → error-preserve/future-version
 */
export function resolveBootSpaces(
  rawSpaces: unknown,
  rawLegacy: unknown,
  legacySpaceId: string,
): BootDecision {
  // --- CASE 1: spaces slot absent ---
  if (rawSpaces === null || rawSpaces === undefined) {
    if (rawLegacy === null || rawLegacy === undefined) {
      // Both absent → seed fresh.
      return { mode: 'seed-fresh' };
    }
    // Legacy present → try to migrate.
    const legacyResult = loadSpaces(null, rawLegacy, legacySpaceId);
    if (legacyResult.ok) {
      return { mode: 'ok', spaces: legacyResult.spaces, source: 'legacy' };
    }
    if (legacyResult.reason === 'future-version') {
      const base = { mode: 'error-preserve', reason: 'future-version' } as const;
      return legacyResult.detail !== undefined
        ? { ...base, detail: legacyResult.detail }
        : base;
    }
    // Legacy invalid → seed fresh (designed failure, both absent effectively).
    return { mode: 'seed-fresh' };
  }

  // --- CASE 2: spaces slot present ---
  const spacesResult = loadSpaces(rawSpaces, null, legacySpaceId);
  if (spacesResult.ok) {
    return { mode: 'ok', spaces: spacesResult.spaces, source: 'spaces' };
  }
  if (spacesResult.reason === 'future-version') {
    const base = { mode: 'error-preserve', reason: 'future-version' } as const;
    return spacesResult.detail !== undefined
      ? { ...base, detail: spacesResult.detail }
      : base;
  }

  // --- CASE 3: rawSpaces is non-null but invalid → attempt legacy recovery ---
  if (rawLegacy === null || rawLegacy === undefined) {
    // Corrupt occupied slot, no legacy → preserve error, do NOT seed fresh.
    return {
      mode: 'error-preserve',
      reason: 'invalid',
      detail: spacesResult.detail ?? 'spaces slot invalid, no legacy board to recover',
    };
  }

  // Attempt legacy path for recovery.
  const recoveryResult = loadSpaces(null, rawLegacy, legacySpaceId);
  if (recoveryResult.ok) {
    // CRITICAL-1: Recovered the legacy board — overwrite the corrupt slot.
    return { mode: 'recovered-legacy', spaces: recoveryResult.spaces };
  }
  if (recoveryResult.reason === 'future-version') {
    const base = { mode: 'error-preserve', reason: 'future-version' } as const;
    return recoveryResult.detail !== undefined
      ? { ...base, detail: recoveryResult.detail }
      : base;
  }
  // Both corrupt.
  return {
    mode: 'error-preserve',
    reason: 'invalid',
    detail: spacesResult.detail ?? 'spaces slot and legacy board both invalid',
  };
}

/* -------------------------------------------------------------------------- */
/* Storage helpers                                                             */
/* -------------------------------------------------------------------------- */

/** Read and JSON-parse from a localStorage key. Returns null on absent/corrupt. */
function readRaw(key: string): unknown | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/**
 * Persist the full Spaces envelope to SPACES_STORAGE_KEY.
 * Returns true on success, false if storage is unavailable or quota exceeded.
 * CRITICAL-2: caller checks the return value and surfaces a banner on false.
 */
function persistSpaces(spaces: Spaces): boolean {
  try {
    localStorage.setItem(SPACES_STORAGE_KEY, JSON.stringify(spaces));
    return true;
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Empty board factory                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Build a valid empty Board for a new space.
 * tiles:[] and layout:[] are schema-valid (BoardSchema has no min on the arrays).
 * The board id is caller-provided so the id namespace lives in the app layer.
 */
function buildEmptyBoard(boardId: string): Board {
  const now = new Date().toISOString();
  const result = {
    schemaVersion: BOARD_SCHEMA_VERSION,
    id: boardId,
    createdAt: now,
    updatedAt: now,
    grid: { cols: 8, rowHeight: 120, gap: 12 },
    layout: [] as const,
    tiles: [] as const,
  };
  return result as unknown as Board;
}

/* -------------------------------------------------------------------------- */
/* Default seed                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Seed a default single "Home" space using buildDefaultBoard (geolocation-aware).
 * Returns a full Spaces envelope with one space.
 */
async function seedDefaultSpaces(): Promise<Spaces> {
  const board = await buildDefaultBoard();
  const spaceId = generateSpaceId();
  const candidate: unknown = {
    schemaVersion: SPACES_SCHEMA_VERSION,
    spaces: [{ id: spaceId, name: 'Home', board }],
    activeSpaceId: spaceId,
  };
  // Validate through the schema (belt-and-suspenders — we own the input).
  const parsed = SpacesSchema.safeParse(candidate);
  if (!parsed.success) {
    throw new Error(`Default spaces failed schema validation: ${parsed.error.message}`);
  }
  return parsed.data;
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                        */
/* -------------------------------------------------------------------------- */

export function useSpacesState(analytics?: AnalyticsEmitter): SpacesState {
  const [status, setStatus] = useState<SpacesStatus>('loading');
  const [spaces, setSpaces] = useState<Spaces | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [persistFailed, setPersistFailed] = useState(false);

  // --------------------------------------------------------------------------
  // Boot load
  // --------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      // Read BOTH slots unconditionally — resolveBootSpaces needs both.
      const rawSpaces = readRaw(SPACES_STORAGE_KEY);
      const rawLegacy = readRaw(BOARD_STORAGE_KEY);
      const legacySpaceId = generateSpaceId();

      const decision = resolveBootSpaces(rawSpaces, rawLegacy, legacySpaceId);

      if (decision.mode === 'ok') {
        const loaded = decision.spaces;
        // On legacy migration (first-run upgrade), persist to the new slot.
        if (decision.source === 'legacy') {
          persistSpaces(loaded);
        }
        if (!cancelled) {
          setSpaces(loaded);
          setStatus('ready');
        }
        return;
      }

      if (decision.mode === 'recovered-legacy') {
        // CRITICAL-1: corrupt spaces slot recovered from legacy board.
        // Overwrite the corrupt slot with the recovered envelope.
        persistSpaces(decision.spaces);
        if (!cancelled) {
          setSpaces(decision.spaces);
          setStatus('ready');
        }
        return;
      }

      if (decision.mode === 'error-preserve') {
        if (decision.reason === 'future-version') {
          if (!cancelled) setStatus('future-version');
        } else {
          // Invalid and no recovery possible — show error, DO NOT persist.
          if (!cancelled) {
            setErrorDetail(decision.detail ?? 'Stored data is corrupt and could not be recovered.');
            setStatus('error');
          }
        }
        return;
      }

      // mode: 'seed-fresh' — seed a default and persist.
      try {
        const seeded = await seedDefaultSpaces();
        persistSpaces(seeded);
        if (!cancelled) {
          setSpaces(seeded);
          setStatus('ready');
        }
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Unknown error seeding spaces';
        if (!cancelled) {
          setErrorDetail(detail);
          setStatus('error');
        }
      }
    }

    void load();
    return () => { cancelled = true; };
  }, []);

  // --------------------------------------------------------------------------
  // Helpers: commit a new Spaces value (persist + setState in one place)
  // CRITICAL-2: track persist failure and surface via persistFailed.
  // Returns true on persist success, false on failure.
  // In-memory state always advances (user keeps edits visible) regardless of
  // persist outcome. Callers MUST check the return value before emitting events —
  // analytics events are only emitted when the change durably persisted.
  // --------------------------------------------------------------------------
  const commit = useCallback((newSpaces: Spaces): boolean => {
    const ok = persistSpaces(newSpaces);
    if (!ok) {
      setPersistFailed(true);
      // Still advance in-memory so user keeps their edits visible.
      // Banner surfaces honesty: reload will lose changes until storage is freed.
    } else {
      setPersistFailed(false);
    }
    setSpaces(newSpaces);
    return ok;
  }, []);

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  const switchSpaceAction = useCallback((spaceId: string, method: 'tab' | 'pill' | 'shortcut' | null) => {
    if (!spaces) return;
    // No-op when clicking the already-active space (no event emitted).
    if (spaceId === spaces.activeSpaceId) return;
    const result = setActiveSpace(spaces, spaceId);
    if (!result.ok) return;
    const persisted = commit(result.spaces);
    // method=null means programmatic/TV resolution — do NOT emit space_switched.
    // Events are only emitted when the change durably persisted (CRITICAL-2 fix).
    if (analytics) {
      const payload = spaceSwitchedEvent(persisted, method, result.spaces.spaces.length);
      if (payload) safeEmit(analytics, withEnvelope(payload, buildEnvelope()));
    }
  }, [spaces, commit, analytics]);

  const createSpaceAction = useCallback((name: string): SpacesActionError => {
    if (!spaces) return 'invalid';
    const spaceId = generateSpaceId();
    const boardId = generateBoardId();
    const emptyBoard = buildEmptyBoard(boardId);
    const newSpace: Space = { id: spaceId, name, board: emptyBoard };
    const result = createSpace(spaces, newSpace);
    if (!result.ok) return result.error;
    const persisted = commit(result.spaces);
    // Only emit when the new space was successfully committed to persistence
    // (§1.S.1 docstring: "successfully committed to persistence").
    if (analytics) {
      const payload = spaceCreatedEvent(persisted, result.spaces.spaces.length);
      if (payload) safeEmit(analytics, withEnvelope(payload, buildEnvelope()));
    }
    return null;
  }, [spaces, commit, analytics]);

  const renameSpaceAction = useCallback(
    (spaceId: string, newName: string, method: 'inline' | 'popover'): SpacesActionError => {
      if (!spaces) return 'invalid';
      const result = renameSpace(spaces, spaceId, newName);
      if (!result.ok) return result.error;
      const persisted = commit(result.spaces);
      // Only emit when the rename was successfully committed to persistence
      // (§1.S.3 docstring: "successfully renamed and persisted").
      if (analytics) {
        const payload = spaceRenamedEvent(persisted, method, result.spaces.spaces.length);
        if (payload) safeEmit(analytics, withEnvelope(payload, buildEnvelope()));
      }
      return null;
    },
    [spaces, commit, analytics],
  );

  const deleteSpaceAction = useCallback((spaceId: string): SpacesActionError => {
    if (!spaces) return 'invalid';
    const wasActive = spaceId === spaces.activeSpaceId;
    const result = deleteSpace(spaces, spaceId);
    if (!result.ok) return result.error;
    const persisted = commit(result.spaces);
    // Only emit when the delete was successfully committed to persistence
    // (§1.S.5 docstring: "confirmed two-step delete commits and persists").
    if (analytics) {
      const payload = spaceDeletedEvent(persisted, result.spaces.spaces.length, wasActive);
      if (payload) safeEmit(analytics, withEnvelope(payload, buildEnvelope()));
    }
    return null;
  }, [spaces, commit, analytics]);

  const duplicateSpaceAction = useCallback((sourceSpaceId: string): SpacesActionError => {
    if (!spaces) return 'invalid';
    const newSpaceId = generateSpaceId();
    const newBoardId = generateBoardId();
    const result = duplicateSpace(spaces, sourceSpaceId, newSpaceId, newBoardId);
    if (!result.ok) return result.error;
    const persisted = commit(result.spaces);
    // Only emit when the duplicate was successfully committed to persistence
    // (§1.S.6 docstring: "successfully duplicated and persisted").
    if (analytics) {
      const payload = spaceDuplicatedEvent(persisted, result.spaces.spaces.length);
      if (payload) safeEmit(analytics, withEnvelope(payload, buildEnvelope()));
    }
    return null;
  }, [spaces, commit, analytics]);

  const reorderSpacesAction = useCallback(
    (orderedIds: readonly string[], fromIndex?: number, toIndex?: number): SpacesActionError => {
      if (!spaces) return 'invalid';
      const result = reorderSpaces(spaces, orderedIds);
      if (!result.ok) return result.error;
      const persisted = commit(result.spaces);
      // Only emit when reorder was durably persisted AND drag metadata provided
      // (§1.S.4 docstring: "NOT fired on cancelled or reverted drags").
      if (analytics) {
        const payload = spaceReorderedEvent(persisted, fromIndex, toIndex, result.spaces.spaces.length);
        if (payload) safeEmit(analytics, withEnvelope(payload, buildEnvelope()));
      }
      return null;
    },
    [spaces, commit, analytics],
  );

  // HIGH-2: updateActiveBoard returns boolean.
  // Returns false if active space was not found (race: user switched spaces).
  // DO NOT advance state in that case — board-op is for the old space.
  const updateActiveBoardAction = useCallback((newBoard: Board): boolean => {
    if (!spaces) return false;
    const result = updateSpaceBoard(spaces, spaces.activeSpaceId, newBoard);
    if (result.ok) {
      commit(result.spaces);
      return true;
    }
    // unknown-space: the active space was switched away before this commit landed.
    // Do NOT advance state. Return false so the caller knows.
    return false;
  }, [spaces, commit]);

  // --------------------------------------------------------------------------
  // Derive active space
  // --------------------------------------------------------------------------
  const activeSpace = spaces
    ? (spaces.spaces.find((s) => s.id === spaces.activeSpaceId) ?? spaces.spaces[0] ?? null)
    : null;

  return {
    status,
    spaces: spaces?.spaces ?? [],
    activeSpace,
    actions: {
      switchSpace: switchSpaceAction,
      createSpace: createSpaceAction,
      renameSpace: renameSpaceAction,
      deleteSpace: deleteSpaceAction,
      duplicateSpace: duplicateSpaceAction,
      updateActiveBoard: updateActiveBoardAction,
      reorderSpaces: reorderSpacesAction,
    },
    errorDetail,
    persistFailed,
  };
}
